import React from "react";
import { Document, Page, Text, View, StyleSheet, Image, Font } from "@react-pdf/renderer";

import Roboto from "/fonts/Roboto-Regular.ttf";

import { APP_NAME } from "../../utils/app.constants";

Font.register({
  family: "Roboto",
  src: Roboto,
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 32,
    fontSize: 8,
  },
  // HEADER
  headerBar: {
    backgroundColor: "#F2ECF9",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    minHeight: 36,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 6,
  },
  logo: {
    width: 28,
    height: 28,
    marginRight: 6,
    objectFit: "contain",
  },
  headerTextBlock: {
    flexDirection: "column",
  },
  headerCompanyName: {
    fontSize: 10,
    fontWeight: "bold",
    letterSpacing: 0.3,
  },
  headerCompanyPhone: {
    fontSize: 8,
    marginTop: 1,
    letterSpacing: 0.3,
  },
  headerRight: {
    alignItems: "flex-end",
    marginRight: 6,
  },
  headerReportTitle: {
    fontSize: 10,
    fontWeight: "bold",
    letterSpacing: 0.3,
  },

  // FILTER LINES (below header)
  filtersContainer: {
    marginTop: 4,
    marginBottom: 6,
  },
  filterLine: {
    fontSize: 8,
    marginTop: 1,
  },
  metaText: {
    fontSize: 8,
    marginTop: 2,
  },

  // TABLE
  table: {
    marginTop: 6,
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    wrap: false,
    fontFamily: "Roboto",
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: "bold",
    paddingVertical: 2,
    paddingHorizontal: 3,
    backgroundColor: "#f2ecf9",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCell: {
    fontSize: 8,
    paddingVertical: 2,
    paddingHorizontal: 3,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    borderLeftWidth: 0,
    borderTopWidth: 0,
    flexWrap: "wrap",
  },
  tableTotalCell: {
    fontWeight: "bold",
    backgroundColor: "#F7F7FC",
  },

  // FOOTER (timestamp + app name, with top margin)
  footerRow: {
    position: "absolute",
    bottom: 18,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#bfbfbf",
    paddingTop: 6,
  },
  footerText: {
    fontSize: 8,
  },
});

export const Listing = ({ title = "", columnHeaders, tableRows, company = {}, meta = [], totals }) => {
  const maxRowsPerPage = 20;
  const chunks = [];
  for (let i = 0; i < tableRows.length; i += maxRowsPerPage) {
    chunks.push(tableRows.slice(i, i + maxRowsPerPage));
  }

  const generatedAt = new Date().toLocaleString();

  const Header = () => {
    const companyLogoUrl = company?.logoFile?.url;
    return (
      <View style={styles.headerBar}>
        <View style={styles.headerLeft}>
          {/* Company Logo */}
          {!!companyLogoUrl && <Image src={companyLogoUrl} style={styles.logo} />}
          {/* Company text */}
          <View style={styles.headerTextBlock}>
            {!!company?.name && <Text style={styles.headerCompanyName}>{company.name}</Text>}
            {!!company?.primaryPhone && <Text style={styles.headerCompanyPhone}>{company.primaryPhone}</Text>}
          </View>
        </View>

        <View style={styles.headerRight}>
          <Text style={styles.headerReportTitle}>{title}</Text>
        </View>
      </View>
    );
  };

  const Table = () => (
    <View style={styles.table}>
      {/* Table header — repeats automatically */}
      <View style={styles.tableRow} fixed>
        {columnHeaders.map((col, idx) => (
          <View key={col.field || idx} style={[styles.tableHeaderCell, { flex: col.flex ?? 1 }]}>
            <Text>{col.headerName || col.field}</Text>
          </View>
        ))}
      </View>

      {/* Table rows */}
      {tableRows.map((row, rowIdx) => (
        <View key={rowIdx} style={styles.tableRow}>
          {columnHeaders.map((col, colIdx) => (
            <View key={col.field || colIdx} style={[styles.tableCell, { flex: col.flex ?? 1 }]}>
              <Text wrap>{row[col.field] ?? ""}</Text>
            </View>
          ))}
        </View>
      ))}

      {/* Optional totals row — opt-in via the `totals` prop, {[field]: displayValue} */}
      {totals && (
        <View style={styles.tableRow}>
          {columnHeaders.map((col, colIdx) => (
            <View key={col.field || colIdx} style={[styles.tableCell, styles.tableTotalCell, { flex: col.flex ?? 1 }]}>
              <Text wrap>{totals[col.field] ?? (colIdx === 0 ? "Total" : "")}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );

  const Footer = () => (
    <View style={styles.footerRow} fixed>
      <Text style={styles.footerText}>{generatedAt}</Text>
      <Text style={styles.footerText}>{APP_NAME}</Text>
    </View>
  );

  return (
    <Document title={title}>
      <Page size="A4" style={styles.page}>
        <View render={({ pageNumber }) => (pageNumber === 1 ? <Header /> : null)} />
        {meta?.length > 0 && (
          <View style={styles.filtersContainer}>
            {meta.map((item, i) => (
              <Text key={i} style={styles.filterLine}>
                {item.label}: {item.value}
              </Text>
            ))}
          </View>
        )}
        <Table />
        <Footer />
      </Page>
    </Document>
  );
};
